import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import AddIcon from "@mui/icons-material/Add";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import TrashIcon from "./TrashIcon";
import "./CheckinForm.css";

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const METER_FIELDS = [
  "reading_gas",
  "reading_electricity",
  "reading_electricity_low",
  "reading_electricity_high",
  "reading_water",
  "reading_heat_pump",
];

const TENANT_FIELDS = ["tenant_1", "tenant_2", "tenant_3"];

// "tentant" is the backend's spelling.
const tenantSignatureField = (tenantField) =>
  `signature_${tenantField.replace("tenant", "tentant")}_blob`;

// Not part of the check-in payload yet.
const SAVED_AT = "15:17";

const RATING_LABEL = {
  good: "Good",
  insufficient: "Insufficient",
  poor: "Poor",
};

const filledCount = (checkin, fields) =>
  fields.filter((field) => {
    const value = checkin?.[field];
    return value !== null && value !== undefined && value !== "";
  }).length;

const normaliseRoom = (room, index) => ({
  id: room.id ?? `r${index + 1}`,
  name: room.name ?? `Room ${index + 1}`,
  // The API calls it condition; the list renders it as good/insufficient/poor.
  rating: room.condition ?? null,
  photos: Array.isArray(room.photos) ? room.photos.length : room.photos ?? 0,
  // Rooms without an order from the API keep their payload position.
  order: Number.isFinite(room.order) ? room.order : index + 1,
});

const byOrder = (a, b) => a.order - b.order;

const CheckinForm = () => {
  const { cfid } = useParams();
  const navigate = useNavigate();

  const { sendRequest: getCheckinRequest, isLoading, error } = useHttpNoAuth();
  const {
    sendRequest: addRoomRequest,
    isLoading: isAddingRoom,
    error: addRoomError,
  } = useHttpNoAuth();
  const {
    sendRequest: deleteRoomRequest,
    isLoading: isDeletingRoom,
    error: deleteRoomError,
  } = useHttpNoAuth();

  const [checkin, setCheckin] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [addingRoom, setAddingRoom] = useState(false);
  const [newRoomName, setNewRoomName] = useState("");
  const [keysComment, setKeysComment] = useState("");
  const [addErrorVisible, setAddErrorVisible] = useState(false);
  const [deletingRoomId, setDeletingRoomId] = useState(null);

  const getCheckin = useCallback(() => {
    if (!cfid) return;
    getCheckinRequest(
      { url: `${BACKEND_URL}/welocate/checkins/${cfid}`, method: "GET" },
      (data) => {
        setCheckin(data);
        setKeysComment(data?.keys_comments ?? "");
        const fetchedRooms = (data?.rooms ?? [])
          .map(normaliseRoom)
          .sort(byOrder);
        setRooms(fetchedRooms);
      }
    );
  }, [cfid, getCheckinRequest]);

  useEffect(() => {
    getCheckin();
  }, [getCheckin]);

  const address = useMemo(() => {
    if (!checkin) return "";
    return [checkin.street, checkin.number, checkin.floor]
      .filter(Boolean)
      .join(" ");
  }, [checkin]);

  // Only the tenants actually on this check-in — there can be one, two or three.
  const presentTenantFields = useMemo(
    () => TENANT_FIELDS.filter((field) => Boolean(checkin?.[field])),
    [checkin]
  );

  const tenants = useMemo(() => {
    const names = presentTenantFields.map((field) => checkin[field]);
    if (names.length < 2) return names.join("");
    return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
  }, [checkin, presentTenantFields]);

  const meters = useMemo(
    () => ({
      filled: filledCount(checkin, METER_FIELDS),
      total: METER_FIELDS.length,
    }),
    [checkin]
  );

  // Welocate signs, plus one signature per tenant present.
  const signatures = useMemo(() => {
    const fields = [
      "welocate_signature",
      ...presentTenantFields.map(tenantSignatureField),
    ];
    return {
      signed: filledCount(checkin, fields),
      total: fields.length,
    };
  }, [checkin, presentTenantFields]);

  const ratedCount = useMemo(
    () => rooms.filter((room) => room.rating !== null).length,
    [rooms]
  );

  // Every rating, meter reading and signature counts equally towards done.
  const progress = useMemo(() => {
    const total = rooms.length + meters.total + signatures.total;
    if (total === 0) return 0;
    const done = ratedCount + meters.filled + signatures.signed;
    return Math.round((done / total) * 100);
  }, [rooms.length, ratedCount, meters, signatures]);

  const deleteRoom = (id) => {
    if (isDeletingRoom) return;
    setDeletingRoomId(id);
    deleteRoomRequest(
      { url: `${BACKEND_URL}/welocate/rooms/${id}`, method: "DELETE" },
      () => {
        setDeletingRoomId(null);
        getCheckin();
      }
    );
  };

  // The hook never rejects, so a failed delete has to release the row here.
  useEffect(() => {
    if (deleteRoomError) setDeletingRoomId(null);
  }, [deleteRoomError]);

  const submitNewRoom = (event) => {
    event.preventDefault();
    const name = newRoomName.trim();
    if (!name || isAddingRoom) return;
    setAddErrorVisible(true);
    addRoomRequest(
      {
        url: `${BACKEND_URL}/welocate/checkins/${cfid}/rooms`,
        method: "POST",
        body: { name },
      },
      () => {
        // The new room — id and order included — comes back with the refetch.
        setAddErrorVisible(false);
        setNewRoomName("");
        setAddingRoom(false);
        getCheckin();
      }
    );
  };

  const startAddingRoom = () => {
    setAddErrorVisible(false);
    setAddingRoom(true);
  };

  const cancelNewRoom = () => {
    setAddErrorVisible(false);
    setNewRoomName("");
    setAddingRoom(false);
  };

  const openRoom = (id) => {
    navigate(`/checkin/${cfid}/rooms/${id}`);
  };

  return (
    <div className="cf-page">
      <header className="cf-topbar">
        {/* The check-in is the top of this flow — nothing to go back to. */}
        <span className="cf-back cf-back--static" aria-hidden="true">
          <HomeOutlinedIcon />
        </span>

        <div className="cf-topbar-titles">
          <h1 className="cf-topbar-title">{address || "Check-in"}</h1>
          <p className="cf-topbar-sub">
            {checkin
              ? `${checkin.postal_code ?? ""} ${
                  checkin.city ?? ""
                } · Check-in`.trim()
              : "Check-in"}
          </p>
        </div>

        <span className="cf-saved">
          <CheckCircleOutlineIcon />
          Saved <span className="cf-saved-time">{SAVED_AT}</span>
        </span>
      </header>

      {isLoading && !checkin && <p className="cf-state">Loading check-in…</p>}

      {!isLoading && error && (
        <div className="cf-state cf-state--error">
          <p className="cf-state-text">{error}</p>
          <button type="button" className="cf-state-retry" onClick={getCheckin}>
            Try again
          </button>
        </div>
      )}

      {checkin && (
        <main className="cf-main">
          <section className="cf-card cf-summary">
            <h2 className="cf-summary-title">{address}</h2>
            <p className="cf-summary-tenants">{tenants}</p>

            <div
              className="cf-progress"
              role="progressbar"
              aria-valuenow={progress}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Inspection progress"
            >
              <div className="cf-progress-bar" style={{ width: `${progress}%` }} />
            </div>

            <div className="cf-stats">
              <div className="cf-stat">
                <div className="cf-stat-value">
                  {ratedCount}/{rooms.length}
                </div>
                <div className="cf-stat-label">RATED</div>
              </div>
              <div className="cf-stat">
                <div className="cf-stat-value">
                  {meters.filled}/{meters.total}
                </div>
                <div className="cf-stat-label">METERS</div>
              </div>
              <div className="cf-stat">
                <div className="cf-stat-value">
                  {signatures.signed}/{signatures.total}
                </div>
                <div className="cf-stat-label">SIGNATURES</div>
              </div>
            </div>
          </section>

          <div className="cf-section-head">
            <h3 className="cf-section-title">ROOMS</h3>
            <span className="cf-section-hint">Tap to fill in</span>
          </div>

          <section className="cf-card">
            {rooms.length === 0 && (
              <p className="cf-empty">No rooms yet — add the first one below.</p>
            )}

            {rooms.map((room, index) => (
              <div className="cf-room" key={room.id}>
                <button
                  type="button"
                  className="cf-room-open"
                  onClick={() => openRoom(room.id)}
                >
                  <span
                    className={`cf-room-index${
                      room.rating ? ` cf-room-index--${room.rating}` : ""
                    }`}
                  >
                    {String(index + 1).padStart(2, "0")}
                  </span>

                  <span className="cf-room-body">
                    <span className="cf-room-name">{room.name}</span>
                    <span className="cf-room-meta">
                      <ImageOutlinedIcon />
                      <span className="cf-room-photos">{room.photos}</span>
                      {room.rating ? RATING_LABEL[room.rating] : "Not started"}
                    </span>
                  </span>

                  <ChevronRightIcon className="cf-room-chevron" />
                </button>

                <button
                  type="button"
                  className="cf-room-delete"
                  aria-label={`Delete ${room.name}`}
                  disabled={isDeletingRoom || isLoading}
                  onClick={() => deleteRoom(room.id)}
                >
                  {deletingRoomId === room.id ? (
                    <span className="cf-spinner" />
                  ) : (
                    <TrashIcon />
                  )}
                </button>
              </div>
            ))}
          </section>

          {!isDeletingRoom && deleteRoomError && (
            <p className="cf-add-error" role="alert">
              {deleteRoomError}
            </p>
          )}

          {addingRoom ? (
            <>
              <form className="cf-add-form" onSubmit={submitNewRoom}>
                <input
                  className="cf-add-input"
                  type="text"
                  autoFocus
                  placeholder="Room name"
                  value={newRoomName}
                  disabled={isAddingRoom}
                  onChange={(event) => setNewRoomName(event.target.value)}
                />
                <button
                  type="submit"
                  className="cf-add-confirm"
                  disabled={isAddingRoom || !newRoomName.trim()}
                >
                  {isAddingRoom ? "Adding…" : "Add"}
                </button>
                <button
                  type="button"
                  className="cf-add-cancel"
                  disabled={isAddingRoom}
                  onClick={cancelNewRoom}
                >
                  Cancel
                </button>
              </form>

              {addErrorVisible && !isAddingRoom && addRoomError && (
                <p className="cf-add-error" role="alert">
                  {addRoomError}
                </p>
              )}
            </>
          ) : (
            <button type="button" className="cf-add" onClick={startAddingRoom}>
              <AddIcon />
              Add a room
            </button>
          )}

          <div className="cf-section-head">
            <h3 className="cf-section-title">HANDOVER</h3>
          </div>

          <section className="cf-card">
            <button
              type="button"
              className="cf-handover-row"
              onClick={() => navigate(`/checkin/${cfid}/meters`)}
            >
              <span className="cf-handover-icon">
                <SpeedOutlinedIcon />
              </span>
              <span className="cf-handover-body">
                <span className="cf-handover-title">Meter readings</span>
                <span className="cf-handover-sub">
                  {meters.filled} of {meters.total} filled in
                </span>
              </span>
              <ChevronRightIcon className="cf-room-chevron" />
            </button>

            <button
              type="button"
              className="cf-handover-row"
              onClick={() => navigate(`/checkin/${cfid}/keys`)}
            >
              <span className="cf-handover-icon">
                <VpnKeyOutlinedIcon />
              </span>
              <span className="cf-handover-body">
                <span className="cf-handover-title">Keys</span>
                <span className="cf-handover-sub">
                  {keysComment.trim() ? "Comment added" : "Comment required"}
                </span>
              </span>
              <ChevronRightIcon className="cf-room-chevron" />
            </button>

            <button
              type="button"
              className="cf-handover-row"
              onClick={() => navigate(`/checkin/${cfid}/signatures`)}
            >
              <span className="cf-handover-icon">
                <EditOutlinedIcon />
              </span>
              <span className="cf-handover-body">
                <span className="cf-handover-title">Remarks &amp; signatures</span>
                <span className="cf-handover-sub">
                  {signatures.signed} signed
                </span>
              </span>
              <ChevronRightIcon className="cf-room-chevron" />
            </button>
          </section>

          <p className="cf-note">
            Rooms are rated <strong>Good</strong>, <strong>Insufficient</strong>{" "}
            or <strong>Poor</strong> — “sufficient” read as a pass and a fail at
            once, so it is gone.
          </p>
        </main>
      )}

      {checkin && (
        <footer className="cf-actions">
          <div className="cf-actions-inner">
            <button type="button" className="cf-btn cf-btn--ghost">
              <SaveOutlinedIcon />
              Save draft
            </button>
            <button type="button" className="cf-btn cf-btn--primary">
              Sign off
            </button>
          </div>
        </footer>
      )}
    </div>
  );
};

export default CheckinForm;
